import { useEffect, useState } from 'react';

import { COLOR_MODE, defaultDark } from './theme';

// Modo escuro. Tailwind está em darkMode:'class', então alternamos a classe `dark` no <html>.
//
// Precedência: escolha explícita do usuário (botão de tema, persistida em localStorage) vence o padrão
// do deploy (`window.__COLOR_MODE__` em config.js: 'light' | 'dark' | 'system'). Com 'system', sem escolha
// salva, o app segue a preferência do SO — inclusive quando ela muda com a página aberta. Só o botão grava
// no storage: aplicar o tema na carga NÃO grava, senão o padrão do deploy viraria "escolha" na 1ª visita.
const STORAGE_KEY = 'sigem-theme';
const SYSTEM_QUERY = '(prefers-color-scheme: dark)';

const readChoice = (): 'dark' | 'light' | null => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === 'dark' || v === 'light' ? v : null;
  } catch {
    return null;
  }
};

/** Escolha salva, ou o padrão do deploy quando não há escolha. */
export const getStoredDark = (): boolean => {
  const choice = readChoice();
  return choice ? choice === 'dark' : defaultDark();
};

/** Modo escuro efetivamente aplicado no <html> agora (lê o DOM, não o storage). */
export const isDarkNow = (): boolean =>
  typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

/** Aplica o modo no <html>. Não persiste: quem quer gravar a escolha chama `storeDark`. */
export const applyDark = (dark: boolean): void => {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', dark);
  // Alinha o color-scheme nativo (scrollbars, inputs) ao tema aplicado,
  // evitando scrollbar escura sobre UI clara (e vice-versa).
  document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
};

/** Grava a escolha explícita do usuário. */
export const storeDark = (dark: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light');
  } catch {
    /* ignore */
  }
};

// Com padrão 'system' e sem escolha salva, acompanha o SO na hora, em qualquer página (inclusive login, que não
// usa o hook). Quem tem estado React (botão de tema) se inscreve para o ícone não ficar desatualizado.
const followers = new Set<(dark: boolean) => void>();
if (COLOR_MODE === 'system' && typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
  const mq = window.matchMedia(SYSTEM_QUERY);
  mq.addEventListener('change', () => {
    if (readChoice() !== null) return;
    applyDark(mq.matches);
    followers.forEach((f) => f(mq.matches));
  });
}

/** [isDark, toggle] — aplica o modo; o toggle persiste a escolha. */
export const useDarkMode = (): readonly [boolean, () => void] => {
  const [dark, setDark] = useState<boolean>(getStoredDark);
  useEffect(() => { applyDark(dark); }, [dark]);
  useEffect(() => {
    followers.add(setDark);
    return () => { followers.delete(setDark); };
  }, []);
  const toggle = () => {
    const next = !dark;
    storeDark(next);
    setDark(next);
  };
  return [dark, toggle] as const;
};
